import { useState } from 'react';
import { Sheet, Button, Segmented, Field, FIELD_CONTROL, Icon, cn } from './ui';

// Inlog- / registratie- / wachtwoord-vergeten-modal. Aangestuurd door AuthProvider.
// De AuthProvider mount deze component pas als hij open moet (conditionele render),
// zodat de state (modus, velden) telkens vers begint op de inlog-modus — anders
// zou wie net registreerde bij de volgende "Inloggen" het registratieformulier zien.
//
// De handgemaakte overlay (met eigen Escape-listener en klik-buiten-sluit) is
// vervangen door Sheet/Radix: focus wordt nu getrapt en teruggegeven, de
// achtergrond is inert voor schermlezers en scrollen achter de dialog is
// geblokkeerd. De tabs zijn een Segmented, dus pijltjestoetsen werken.
export default function AuthModal({ open, onClose, onSignIn, onSignUp, onForgot }) {
  const [mode, setMode]         = useState('login'); // 'login' | 'register' | 'forgot'
  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [name, setName]         = useState('');
  const [showPw, setShowPw]     = useState(false);
  const [busy, setBusy]         = useState(false);
  const [error, setError]       = useState(null);
  const [info, setInfo]         = useState(null);

  if (!open) return null;

  function switchMode(next) {
    setMode(next); setError(null); setInfo(null); setPassword(''); setShowPw(false);
  }

  async function submit(e) {
    e.preventDefault();
    setBusy(true); setError(null); setInfo(null);
    try {
      if (mode === 'login') {
        const { ok, error } = await onSignIn(email.trim(), password);
        if (!ok) setError(error);
      } else if (mode === 'register') {
        const { ok, error } = await onSignUp(email.trim(), password, name.trim());
        if (!ok) setError(error);
      } else {
        const { ok, error } = await onForgot(email.trim());
        if (ok) setInfo('Als er een account bij dit e-mailadres hoort, is er een reset-link verstuurd. Check ook je spam.');
        else setError(error);
      }
    } catch {
      setError('Geen verbinding. Probeer opnieuw; je bent nog niet aangemeld.');
    } finally {
      setBusy(false);
    }
  }

  const title = mode === 'login' ? 'Welkom terug'
    : mode === 'register' ? 'Account aanmaken'
    : 'Wachtwoord vergeten';
  const subtitle = mode === 'login' ? 'Bekijk je eigen ritten, records en badges. Beheer je transponder, privacy en coaches.'
    : mode === 'register' ? 'Bewaar je ritten op één plek. Maak je account, koppel je transponder en ontdek je eerste gemeten ronde.'
    : 'Vul je e-mailadres in — we sturen je een reset-link.';
  const submitLabel = mode === 'login' ? 'Inloggen'
    : mode === 'register' ? 'Account aanmaken'
    : 'Reset-link versturen';

  return (
    <Sheet open onOpenChange={o => !o && onClose()} size="sm" title={title} description={subtitle}>
      <form onSubmit={submit} className="flex flex-col gap-4">
        {mode !== 'forgot' && (
          <Segmented
            ariaLabel="Inloggen of registreren"
            value={mode}
            onChange={switchMode}
            options={[
              { value: 'login', label: 'Inloggen' },
              { value: 'register', label: 'Registreren' },
            ]}
            className="self-stretch"
          />
        )}

        {mode === 'register' && (
          <Field label="Naam">
            {props => (
              <input
                {...props}
                value={name}
                onChange={e => setName(e.target.value)}
                className={FIELD_CONTROL}
                placeholder="Je naam"
                autoComplete="name"
              />
            )}
          </Field>
        )}

        <Field label="E-mailadres">
          {props => (
            <input
              {...props}
              type="email"
              required
              value={email}
              onChange={e => setEmail(e.target.value)}
              className={FIELD_CONTROL}
              placeholder="jij@voorbeeld.nl"
              autoComplete="email"
              autoFocus
            />
          )}
        </Field>

        {mode !== 'forgot' && (
          <Field label="Wachtwoord">
            {props => (
              <div className="relative flex w-full items-center">
                <input
                  {...props}
                  type={showPw ? 'text' : 'password'}
                  required
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  className={cn(FIELD_CONTROL, 'pr-12')}
                  placeholder={mode === 'register' ? 'Minstens 8 tekens' : '••••••••'}
                  autoComplete={mode === 'register' ? 'new-password' : 'current-password'}
                  minLength={8}
                />
                <button
                  type="button"
                  onClick={() => setShowPw(s => !s)}
                  className="absolute right-0 flex h-11 w-11 cursor-pointer items-center justify-center rounded-pill text-ink-3 hover:bg-surface-3 hover:text-ink focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-500"
                  aria-label={showPw ? 'Wachtwoord verbergen' : 'Wachtwoord tonen'}
                  aria-pressed={showPw}
                >
                  <Icon name={showPw ? 'eyeOff' : 'eye'} size={16} />
                </button>
              </div>
            )}
          </Field>
        )}

        {mode === 'login' && (
          <Button variant="ghost" size="sm" className="self-end" onClick={() => switchMode('forgot')}>
            Wachtwoord vergeten?
          </Button>
        )}

        {error && (
          <p className="rounded-md border border-red-500/25 bg-red-100 px-3 py-2.5 text-[14.5px] leading-relaxed text-red-600" role="alert">
            {error}
          </p>
        )}
        {info && (
          <p role="status" className="rounded-md border border-green-500/25 bg-green-100 px-3 py-2.5 text-[14.5px] leading-relaxed text-green-600">
            {info}
          </p>
        )}

        <Button type="submit" variant="accent" size="lg" block disabled={busy}>
          {busy ? 'Even geduld…' : submitLabel}
        </Button>

        {mode === 'forgot' && (
          <Button variant="ghost" size="sm" className="self-center" onClick={() => switchMode('login')}>
            ← Terug naar inloggen
          </Button>
        )}
      </form>
    </Sheet>
  );
}
